import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import DashboardSidebar from '@/components/DashboardSidebar';
import DashboardTopNav from '@/components/DashboardTopNav';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { BookOpen, FileText, Clock, Users } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { SearchBar } from '@/components/SearchBar';

interface Article {
  title: string;
  path?: string;
}

interface Category {
  title: string;
  description: string;
  icon: LucideIcon;
  path?: string;
  articles: Article[];
}

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const categories: Category[] = [
    {
      title: "Getting Started",
      description: "Essential information for new applicants",
      icon: BookOpen,
      path: "/knowledge-base/getting-started",
      articles: [
        {
          title: "Understanding Karta Pobytu Requirements",
          path: "/knowledge-base/karta-pobytu-requirements"
        },
        {
          title: "Document Checklist for First-Time Applicants",
          path: "/knowledge-base/document-checklist"
        },
        {
          title: "Step-by-Step Application Process",
          path: "/knowledge-base/application-process"
        },
        {
          title: "Common Mistakes to Avoid",
          path: "/knowledge-base/common-mistakes"
        }
      ]
    },
    {
      title: "Documentation",
      description: "Everything about required documents",
      icon: FileText,
      path: "/knowledge-base/documentation",
      articles: [
        { title: "Passport and ID Requirements" },
        { title: "Proof of Income Documentation" },
        { title: "Housing Documentation Guide" },
        { title: "Health Insurance Requirements" }
      ]
    },
    {
      title: "Processing Times",
      description: "Timeline and processing information",
      icon: Clock,
      path: "/knowledge-base/processing-times",
      articles: [
        { 
          title: "Average Processing Times by Voivodeship",
          path: "/knowledge-base/processing-times/voivodeship-times"
        },
        { 
          title: "How to Track Your Application Status",
          path: "/knowledge-base/processing-times/track-status"
        },
        { 
          title: "Expedited Processing Options",
          path: "/knowledge-base/processing-times/expedited-options"
        },
        { 
          title: "What to Do If Processing is Delayed",
          path: "/knowledge-base/processing-times/handling-delays"
        }
      ]
    },
    {
      title: "Support Resources",
      description: "Additional help and resources",
      icon: Users,
      path: "/knowledge-base/support-resources",
      articles: [
        { 
          title: "Contact Information for Each Office",
          path: "/knowledge-base/support-resources/office-contacts"
        },
        { 
          title: "Legal Support Options",
          path: "/knowledge-base/support-resources/legal-support"
        },
        { 
          title: "Community Resources",
          path: "/knowledge-base/support-resources/community-resources"
        },
        { 
          title: "FAQ for Common Questions",
          path: "/knowledge-base/support-resources/faq"
        }
      ]
    }
  ];

  // Content component to avoid duplication
  const KnowledgeBaseContent = () => (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-foreground mb-4 animate-slide-in-right">
          Knowledge Base
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto animate-fade-in mb-8" style={{ animationDelay: '200ms' }}>
          Find comprehensive guides and resources for your Polish residence card application
        </p>
        
        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <SearchBar />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {categories.map((category, index) => {
          const IconComponent = category.icon;
          return (
            <Card 
              key={index} 
              className="hover:shadow-lg transition-all duration-300 hover:scale-105 animate-scale-in border-border cursor-pointer" 
              style={{ animationDelay: `${index * 150}ms` }}
              onClick={() => category.path && navigate(category.path)}
            >
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <IconComponent className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-foreground">{category.title}</CardTitle>
                    <CardDescription className="text-muted-foreground">{category.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {category.articles.map((article, articleIndex) => (
                    <li 
                      key={articleIndex} 
                      className="text-primary hover:text-primary/80 cursor-pointer transition-colors duration-200 hover:scale-105 animate-fade-in"
                      style={{ animationDelay: `${(index * 4 + articleIndex) * 100}ms` }}
                      onClick={() => article.path && navigate(article.path)}
                    >
                      • {article.title}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-16 bg-card rounded-lg shadow-sm p-8 animate-fade-in border border-border" style={{ animationDelay: '800ms' }}>
        <h2 className="text-2xl font-bold text-foreground mb-6">Can't Find What You're Looking For?</h2>
        <p className="text-muted-foreground mb-4">
          Our knowledge base is constantly updated with new information. If you can't find the answer to your question, 
          please don't hesitate to contact our support team.
        </p>
        <div className="flex space-x-4">
          <button 
            onClick={() => navigate('/help-center')}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-all duration-200 hover:scale-105"
          >
            Contact Support
          </button>
          <button 
            onClick={() => navigate('/contact')}
            className="border border-border text-muted-foreground px-6 py-2 rounded-lg hover:bg-accent transition-all duration-200 hover:scale-105"
          >
            Submit a Request
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {user ? (
        // Dashboard layout for logged-in users
        <SidebarProvider>
          <div className="min-h-screen flex w-full bg-background">
            <DashboardSidebar />
            <SidebarInset className="flex-1 flex flex-col">
              <DashboardTopNav pageTitle="Knowledge Base" />
              <main className="flex-1 p-4 sm:p-8 mt-[3.5rem] sm:mt-[4.2rem] overflow-auto">
                <KnowledgeBaseContent />
              </main>
            </SidebarInset>
          </div>
        </SidebarProvider>
      ) : (
        // Regular layout for non-logged-in users
        <div className="min-h-screen bg-background flex flex-col">
          <Header />
          <div className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fade-in">
            <KnowledgeBaseContent />
          </div>
          <Footer />
        </div>
      )}
    </>
  );
};

export default KnowledgeBase;
